import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { useRecruiter } from '../context/RecruiterContext.tsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card.tsx';
import Button from '../components/ui/button.tsx';
import Separator from '../components/ui/button.tsx';
import { AlertCircle, FileText, Upload } from 'lucide-react';

interface JobToResumesProps {
    isLoggedIn: boolean;
}

export function HistoricalSearch({ isLoggedIn }: JobToResumesProps) {



    const [jobDescriptionFile, setJobDescriptionFile] = useState<File | null>(null);
    const [jobDescriptionText, setJobDescriptionText] = useState('');
    const [message, setMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const isRecruiter = useRecruiter();

    useEffect(() => {
        if (!isRecruiter.isRecruiter) {
            const message = 'You are not authorized to view this page. If you are a recruiter contact us on support@cvseeyou.com to get started today!';
            const encodedMessage = btoa(message);
            navigate('/?message=' + encodedMessage);
        }
    }, [isRecruiter, navigate]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setJobDescriptionFile(event.target.files?.[0] || null);
    };

    const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setJobDescriptionText(event.target.value);
    };

    const handleRecruiterUpload = async () => {
        if (!jobDescriptionFile && !jobDescriptionText.trim()) {
            setMessage('Please provide the job description as a file or text before uploading.');
            return;
        }

        const formData = new FormData();

        setIsUploading(true);
        setMessage('Uploading file...');

        if (!jobDescriptionFile && jobDescriptionText.trim()) {
            const jobDescriptionPDF = await textToPDF(jobDescriptionText, 'resume.pdf');
            formData.append('jobDescription', jobDescriptionPDF);
        } else if (jobDescriptionFile) {
            formData.append('jobDescription', jobDescriptionFile);
        }

        try {
            const response = await fetch('/recruiterUpload', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                const resultsUrl = `/results?docId=${data.jobDescriptionId}&docType=resume&historical=true`;
                // Then navigate to the search page
                navigate(resultsUrl);
            }
        } catch (error: any) {
            setMessage('Error uploading file.');
            console.error('Error:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const textToPDF = async (text: string, filename: string) => {
        try {
            const doc = new jsPDF();
            doc.text(text, 10, 10);
            const blob = doc.output('blob');
            const timestamp = new Date().toISOString().replace(/[-:.]/g, '');
            const timestampedFilename = filename.replace('.pdf', `_${timestamp}.pdf`);
            return new File([blob], timestampedFilename, { type: 'application/pdf' });
        } catch (error) {
            console.error('Error during PDF conversion:', error);
            throw new Error('PDF conversion failed');
        }
    };

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [jobDescriptionText]);

    useEffect(() => {
        const handleFileOrTextUpdate = () => {
            if (fileInputRef.current && fileInputRef.current.value) {
                setJobDescriptionText('');
                if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                }
            } else if (textareaRef.current && textareaRef.current.value) {
                setJobDescriptionFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = ''; // Clear the file input
                }
            }
        };

        handleFileOrTextUpdate();
    }, [jobDescriptionFile, jobDescriptionText]);
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-4 md:p-8">
            <Card className="max-w-3xl mx-auto">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="h-6 w-6" />
                        Upload Resume
                    </CardTitle>
                    <CardDescription>
                        Upload a Resume to search for historical roles the Canidate might be a good fit for.
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    <div className="space-y-6">
                        {/* File Upload Section */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium">Option 1: Upload File</h3>
                            <div className="relative">
                                <input
                                    type="file"
                                    id="jobDescription"
                                    name="jobDescription"
                                    accept=".pdf,.docx,.txt"
                                    onChange={handleFileChange}
                                    ref={fileInputRef}
                                    className="hidden"
                                />
                                <Button
                                    onClick={() => fileInputRef.current?.click()}
                                    variant="outline"
                                    className="w-full h-24 border-2 border-dashed"
                                >
                                    <div className="flex flex-col items-center gap-2">
                                        <Upload className="h-6 w-6" />
                                        <span>Click to upload or drag and drop</span>
                                        <span className="text-sm text-gray-500">PDF, DOCX, or TXT files</span>
                                    </div>
                                </Button>
                            </div>
                            {jobDescriptionFile && (
                                <p className="text-sm text-green-600 flex items-center gap-2">
                                    <FileText className="h-4 w-4" />
                                    {jobDescriptionFile.name}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center gap-4">
                            <Separator className="flex-grow"> </Separator>
                            <span className="text-sm text-gray-500">OR</span>
                            <Separator className="flex-grow"> </Separator>
                        </div>

                        {/* Text Input Section */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium">Option 2: Paste Text</h3>
                            <textarea
                                id="jobDescriptionText"
                                name="jobDescriptionText"
                                placeholder="Paste your job description here..."
                                className="w-full min-h-[200px] p-4 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                value={jobDescriptionText}
                                onChange={handleTextChange}
                                ref={textareaRef}
                            />
                        </div>

                        {/* Upload Button */}
                        <Button
                            onClick={handleRecruiterUpload}
                            disabled={isUploading || (!jobDescriptionFile && !jobDescriptionText.trim())}
                            className="w-full"
                        >
                            {isUploading ? (
                                <div className="flex items-center gap-2">
                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Processing...
                                </div>
                            ) : (
                                'Find Matching Job Descriptions'
                            )}
                        </Button>

                        {/* Error/Success Message */}
                        {message && (
                            <div className={`flex items-center gap-2 p-4 rounded-lg ${message.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                                }`}>
                                <AlertCircle className="h-5 w-5" />
                                <p className="text-sm">{message}</p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}